import React, { useRef } from 'react';
import styled from 'styled-components';

import BucketIcon from '@svg/bucket_icon';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

import { Chip, Divider, Skeleton, Stack } from '@mui/material';
import { useTheme } from '../../../../../styles/theme';
import { DescriptionOutlined, Dns } from '@mui/icons-material';
import { TelnyxBucketMatch } from '@store/slices';

import Markdown from '@common/Node/CompletionChatbot/Markdown';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Accordion = styled(MuiAccordion)<{ slotProps?: any }>`
  border: 1px solid ${(props) => props.theme.line.light};
  background-color: ${({ theme }) => theme.background.standard} !important;
  width: 100%;

  &::before {
    display: none;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.background.standard} !important;
  width: 100%;

  & .MuiAccordionSummary-expandIconWrapper.Mui-expanded {
    transform: rotate(90deg);
  }

  & .MuiAccordionSummary-content {
    background-color: ${({ theme }) => theme.background.standard} !important;
    justify-content: space-between;
    padding: 0.5rem;
  }

  .MuiAccordionSummary-expandIconWrapper {
    & svg {
      font-size: 0.9rem;
    }
  }
`;

const AccordionDetails = styled(MuiAccordionDetails)`
  padding: 0 !important;
  border-top: 1px solid rgba(0, 0, 0, 0.125);
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  h1 {
    font-size: 1.6ch;
    font-family: 'Inter';
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;

  p {
    font-size: 1.4ch;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
`;

const Panels = styled.div`
  display: flex;
  flex-direction: column;

  overflow-y: scroll;
  max-height: calc(100vh - 61px - 32px - 200px);
`;

const Panel = styled.div`
  display: flex;
  justify-content: flex-start;

  white-space: pre-wrap;
  word-break: break-word;

  img {
    max-height: 200px;
    max-width: 500px;
  }

  .side {
    border-right: 1px solid ${({ theme }) => theme.line.light};
    padding-right: 24px;
    position: sticky;
    top: 0;
    padding: 24px;
    min-width: 100px;
  }

  .main {
    flex-direction: column;
    font-size: 15px;
    padding: 24px;
    max-width: 1000px;

    white-space: 'normal';

    ul,
    li {
      white-space: normal;
    }

    h3 {
      font-size: 2ch;
      text-decoration: underline;
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    font-family: 'Inter';
  }

  p {
    font-family: 'Inter';
  }
`;

interface Props {
  id: string;
  title: string;
  filename: string;
  document: string;
  index: number;
  describe: string;
  matched: TelnyxBucketMatch;
  expanded: string | null;
  setExpanded: React.Dispatch<string | null>;
}

export const Result: React.FC<Props> = ({
  id,
  index,
  title,
  filename,
  document,
  describe,
  matched,
  expanded,
  setExpanded,
}) => {
  const theme = useTheme();
  const accordionRef = useRef<any | null>(null);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : null);
    if (newExpanded && accordionRef?.current) {
      setTimeout(() => accordionRef?.current?.scrollIntoView({ behavior: 'smooth', alignToTop: true }), 500);
    }
  };

  const stripQuotes = (str: string) => {
    if (!str) return filename;

    if (str.startsWith('"') && str.endsWith('"')) return str.slice(1, -1).trim();
    if (str.startsWith('"')) return str.substring(1, str.length).trim();
    if (str.endsWith('"')) return str.substring(0, str.length - 1).trim();

    return str.trim();
  };

  return (
    <Accordion
      expanded={expanded === id}
      onChange={handleChange(id)}
      elevation={0}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary ref={accordionRef}>
        <Header>
          <h1>{title === 'No Title' ? filename : stripQuotes(title)}</h1>
          <Actions>
            {matched?.chunk?.bucket && (
              <Stack direction='row' spacing={0.6} alignItems='center'>
                <BucketIcon height={13} />
                <p>{matched.chunk.bucket}</p>
              </Stack>
            )}
            {matched?.chunk?.index && (
              <Stack direction='row' spacing={0.6} alignItems='center'>
                <Dns sx={{ fontSize: '1.5ch' }} />
                <p>{matched.chunk?.index.toLowerCase()}</p>
              </Stack>
            )}
            {title !== 'No Title' && <Divider orientation='vertical' flexItem />}
            {title !== 'No Title' && (
              <Stack direction='row' spacing={0.6} alignItems='center'>
                <DescriptionOutlined sx={{ fontSize: '1.4ch' }} />
                <p>{filename.replace('https://developers.telnyx.com/', '')}</p>
              </Stack>
            )}
          </Actions>
        </Header>
        <Footer>
          <Chip
            sx={{ borderRadius: '6px', fontWeight: 'bold', color: theme.text.standard }}
            variant='outlined'
            label={`#${index + 1}`}
          />
          <Chip
            sx={{ borderRadius: '6px', fontWeight: 'bold' }}
            variant='outlined'
            label={`${(matched.chunk.certainty * 100).toFixed(8)}%`}
          />
        </Footer>
      </AccordionSummary>
      <AccordionDetails>
        <Panels>
          <Panel>
            <div className='side'>
              <h3>Matched</h3>
            </div>
            <div className='main'>
              {matched.chunk.heading !== 'N/A' ? (
                <p>
                  <strong>{matched.chunk.heading}</strong>
                  {`\n${matched.chunk.content}`}
                </p>
              ) : (
                <p>{matched.chunk.content}</p>
              )}
            </div>
          </Panel>
          <Divider flexItem orientation='horizontal' />
          <Panel>
            <div className='side'>
              <h3>Describe</h3>
            </div>
            <div className='main'>
              <p>{describe}</p>
            </div>
          </Panel>
          <Divider flexItem orientation='horizontal' />
          <Panel>
            <div className='side'>
              <h3>Document</h3>
            </div>
            <div className='main'>
              <Markdown content={document} />
            </div>
          </Panel>
        </Panels>
      </AccordionDetails>
    </Accordion>
  );
};

export const ResultLoading: React.FC = () => {
  return (
    <Accordion expanded={false} disabled elevation={0}>
      <AccordionSummary>
        <Header>
          <Skeleton animation='wave' variant='text' sx={{ fontSize: '1rem', maxWidth: 200 }} />
          <Skeleton animation='wave' variant='text' sx={{ fontSize: '1rem', minWidth: 600 }} />
        </Header>
        <Footer>
          <Skeleton animation='wave' variant='rounded' width={40} height={32} />
          <Skeleton animation='wave' variant='rounded' width={91} height={32} />
        </Footer>
      </AccordionSummary>
    </Accordion>
  );
};
